import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Search, Users, ExternalLink, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { AppLayout } from "@/components/layout/AppLayout";
import { ListShell } from "@/components/shared/ListShell";
import { ListPagination } from "@/components/shared/ListPagination";
import { DataTable, type DataTableColumn } from "@/components/shared/DataTable";
import { PermissionGate } from "@/components/rbac/PermissionGate";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { formatDate } from "@/utils/format";
import {
  useOwnerList,
  useDeleteOwner,
  type OwnerEntity,
} from "@/hooks/useOwners";
import { OwnerCreateDialog } from "./OwnerCreateDialog";

const DEFAULT_PAGE_SIZE = 25;

export const OwnersList = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(DEFAULT_PAGE_SIZE);
  const [createOpen, setCreateOpen] = useState(false);

  const { data, isLoading, error, refetch } = useOwnerList({
    search: search || undefined,
    page,
    limit,
  });

  const deleteOwner = useDeleteOwner();

  const handleDelete = (id: string, name: string) => {
    if (
      !confirm(
        `Are you sure you want to delete owner "${name}" and ALL their businesses, subscriptions, invoices, and data? This action CANNOT be undone.`,
      )
    ) {
      return;
    }

    deleteOwner.mutate(id, {
      onSuccess: () => {
        toast.success(`Owner "${name}" and all details deleted successfully`);
        refetch();
      },
      onError: (err) => toast.error(err.message),
    });
  };

  const rows = data?.data ?? [];
  const pagination = data?.pagination;

  const columns: Array<DataTableColumn<OwnerEntity>> = [
    {
      key: "name",
      header: "Name",
      sortValue: (o) => o.name,
      render: (o) => (
        <Link
          to={`/owners/${o.id}`}
          className="font-medium text-foreground hover:underline"
          onClick={(e) => e.stopPropagation()}
        >
          {o.name}
        </Link>
      ),
    },
    {
      key: "phone",
      header: "Phone",
      sortValue: (o) => o.phone,
      render: (o) => <span className="text-muted-foreground">{o.phone}</span>,
    },
    {
      key: "email",
      header: "Email",
      sortValue: (o) => o.email ?? "",
      render: (o) => (
        <span className="text-muted-foreground">{o.email || "—"}</span>
      ),
    },
    {
      key: "businesses",
      header: "Businesses",
      align: "right",
      sortValue: (o) => o._count?.businesses ?? 0,
      render: (o) => (
        <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 text-primary px-2 py-0.5 text-xs font-medium">
          <Users className="w-3 h-3" />
          {o._count?.businesses ?? 0}
        </span>
      ),
    },
    {
      key: "plan",
      header: "Plan",
      render: (o) => {
        const sub = o.subscriptions?.[0];
        if (!sub) return <span className="text-muted-foreground">—</span>;
        const planName = sub.plan?.name ?? (sub.isCustom ? "Custom" : "—");
        return (
          <div className="flex items-center gap-1.5">
            <span className="text-xs font-medium">{planName}</span>
            <StatusBadge status={sub.status} />
          </div>
        );
      },
    },
    {
      key: "joined",
      header: "Joined",
      sortValue: (o) => new Date(o.createdAt),
      render: (o) => (
        <span className="text-muted-foreground">{formatDate(o.createdAt)}</span>
      ),
    },
    {
      key: "actions",
      header: "Actions",
      align: "right",
      render: (o) => (
        <div className="flex items-center justify-end gap-2">
          <Link
            to={`/owners/${o.id}`}
            className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
            onClick={(e) => e.stopPropagation()}
          >
            View <ExternalLink className="w-3 h-3" />
          </Link>
          <PermissionGate module="owners" action="delete">
            <button
              type="button"
              className="p-1 rounded text-muted-foreground hover:text-destructive hover:bg-destructive/10"
              title="Delete owner and all details"
              onClick={(e) => {
                e.stopPropagation();
                handleDelete(o.id, o.name);
              }}
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </PermissionGate>
        </div>
      ),
    },
  ];

  return (
    <AppLayout title="Owners">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="relative w-full sm:w-72">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            placeholder="Search by name, phone, email…"
            className="pl-8"
          />
        </div>
        <PermissionGate module="owners" action="create">
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="w-4 h-4 mr-1.5" /> Create Owner
          </Button>
        </PermissionGate>
      </div>

      <ListShell
        isLoading={isLoading}
        error={error}
        isEmpty={rows.length === 0}
        onRetry={() => refetch()}
        emptyTitle="No owners yet"
        emptyDescription="Create an owner first, then link businesses under them."
      >
        <DataTable
          columns={columns}
          rows={rows}
          rowKey={(o) => o.id}
          onRowClick={(o) => navigate(`/owners/${o.id}`)}
        />

        <ListPagination
          pagination={pagination}
          page={page}
          rowsShown={rows.length}
          itemLabel="owners"
          onPageChange={setPage}
          limit={limit}
          onLimitChange={(n) => {
            setLimit(n);
            setPage(1);
          }}
        />
      </ListShell>

      <OwnerCreateDialog open={createOpen} onOpenChange={setCreateOpen} />
    </AppLayout>
  );
};

export default OwnersList;
